import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AssignedContact, ClientTaskGroup, ClientTaskGroupType, TaskStatus, WidgetData } from './client-task.models';
import { viewPortal } from '@onviobr/services/session';
import { ClientTaskWidgetService } from './client-task-widget.service';
import { has as hasFrp} from '@onviobr/services/permission';
@Component({
	selector: 'app-client-task-widget',
	templateUrl: './client-task-widget.component.html',
	styleUrls: ['./client-task-widget.component.css']
})
export class ClientTaskWidgetComponent implements OnChanges, OnInit, OnDestroy {

	@Input() client: cs.IClientWithExpansionV3Dto;
	@ViewChild('searchInput', {static: true}) searchInput: ElementRef<HTMLInputElement>;
	@ViewChild('statusFilter', {static: true}) statusFilter: ElementRef<HTMLSelectElement>;

	viewPortal = viewPortal;
	hasFrp = hasFrp;
	clientCenterTasks: ClientTaskGroup[] = [];
	taskGroups: WidgetData[] = [];
	filteredTaskGroups: WidgetData[] = [];
	filteredByStatus: WidgetData[] = [];
	filteredBySearch: WidgetData[] = [];
	showBusyLoader = false;
	statusList: { key: TaskStatus | 5; value: string }[] = [];
	currSearchTerm: string;
	currStatus: string;
	companyId: string;
	userId: string;
	showViewAs: boolean;

	constructor(
		private taskService: ClientTaskWidgetService) {
		const statusFilter = localStorage.getItem('cc-task-widget-status');
		if (statusFilter) {
			this.currStatus = statusFilter;
		} else {
			this.currStatus = '5';
		}
	}

	async ngOnInit() {
		this.statusList = await this.taskService.getStatusList();
		this.showViewAs = await this.hasFrp('OnvioSetup.ViewPortal');
	}

	async ngOnChanges() {
		this.taskGroups = [];
		this.filteredBySearch = [];
		this.filteredByStatus = [];
		this.filteredTaskGroups = [];
		this.currSearchTerm = '';
		if (this.searchInput) {
			this.searchInput.nativeElement.value = '';
		}
		if (this.statusFilter) {
			this.statusFilter.nativeElement.value = this.currStatus;
		}
		if (this.client) {
			this.companyId = this.client.companyId;
			this.showBusyLoader = true;
			this.clientCenterTasks = await this.taskService.getClientCenterTasks(this.client.id);
			this.taskGroups = await this.taskService.getWidgetTaskGroups(this.clientCenterTasks, this.client);
			this.filteredTaskGroups = this.taskGroups;
			this.filteredBySearch = this.taskGroups;
			this.filteredByStatus = this.taskGroups;
			this.showBusyLoader = false;
			this.getAsyncSubjects();
			this.statusFilterChanged(this.currStatus);
		}
	}

	getAsyncSubjects() {
		this.taskGroups.forEach(async t => {
			if (t.type === ClientTaskGroupType.Document_Request) {
				const ccTask = this.clientCenterTasks.find(task => task.id === t.id);
				try {
					t.subject = await this.taskService.getDocRequestSubject(ccTask);
				} catch {
					t.canEdit = false;
				}
			}
			if (t.type === ClientTaskGroupType.Document_Review) {
				const ccTask = this.clientCenterTasks.find(task => task.id === t.id);
				try {
					t.subject = await this.taskService.getDocReviewSubject(ccTask);
				} catch {}
			}
		});
	}

	statusFilterChanged(status: string) {
		this.currStatus = status;
		const statusInt = parseInt(status, 10);
		if (statusInt === 5) {
			this.filteredByStatus = this.taskGroups;
		} else {
			this.filteredByStatus = this.taskGroups.filter(t => t.status === statusInt);
		}
		this.filteredTaskGroups = this.filteredByStatus.filter(t => this.filteredBySearch.includes(t));
	}

	async editTask(taskGroup: WidgetData) {
		const group = this.clientCenterTasks.find(t => t.id === taskGroup.id);
		if (taskGroup.type === ClientTaskGroupType.Document_Request) {
			const id = group.tasks[0].params?.documentRequestId;
			window.open(`#/documents/communications/request/${id}`, '_blank');
		}
	}

	searchFilterChanged(value: string) {
		if (value === '') {
			this.currSearchTerm = value;
			this.filteredBySearch = this.taskGroups;
		} else if (value.includes(this.currSearchTerm)) {
			this.currSearchTerm = value;
			this.filteredBySearch = this.filteredBySearch.filter(t => this.includesSearchTerm(t, value));
		} else {
			this.currSearchTerm = value;
			this.filteredBySearch = this.taskGroups.filter(t => this.includesSearchTerm(t, value));
		}
		this.filteredTaskGroups = this.filteredByStatus.filter(t => this.filteredBySearch.includes(t));
	}

	includesSearchTerm(group: WidgetData, searchTerm: string) {
		const lowerCaseTerm = searchTerm.toLowerCase();
		return group.statusLabel?.toLowerCase().includes(lowerCaseTerm) ?
			true :
			group.dueDate?.includes(lowerCaseTerm) ?
				true :
				group.label?.toLowerCase().includes(lowerCaseTerm) ?
					true :
					group.assignedContacts?.find(contact => contact.name?.toLowerCase().includes(lowerCaseTerm)) ?
						true :
						group.subject?.toLowerCase().includes(lowerCaseTerm) ?
							true :
							false;
	}

	async viewClientCenterAs(contact: AssignedContact, taskId: string) {
		const widgetTask = this.taskGroups.find(t => t.id === taskId);
		const childTaskId = this.clientCenterTasks.find(t => t.id === taskId).tasks[0].taskId;
		try {
			widgetTask.showBusyLoader = true;
			await this.viewPortal(contact.id, this.companyId, contact.userId, {taskId: childTaskId});
			widgetTask.showBusyLoader = false;
		} catch {
			widgetTask.showBusyLoader = false;
		}
	}

	async getCanceledEsignInfo(task: WidgetData) {
		if (!task.esignInfo) {
			const ccTask = this.clientCenterTasks.find(t => t.id === task.id);
			task.esignInfo = await this.taskService.getCanceledEsignMessage(ccTask);
		}
	}

	ngOnDestroy() {
		localStorage.setItem('cc-task-widget-status', this.currStatus);
	}

	showDueDate(task: WidgetData) {
		return task?.type === ClientTaskGroupType.Payment_Request
			|| task?.type === ClientTaskGroupType.Questionnaire
			|| task?.type === ClientTaskGroupType.Document_Request;
	}

	showEsignInfoIcon(taskGroup: WidgetData) {
		return taskGroup.type === ClientTaskGroupType.Esign && taskGroup.status === TaskStatus.Canceled;
	}

	checkEllipse(el: HTMLElement, data: WidgetData | AssignedContact) {
		if (!data.tooltipChecked) {
			if (el.offsetWidth < el.scrollWidth) {
				data.showTooltip = true;
			}
		}
	}

	getMaxWidth() {
		return this.taskService.getMaxWidth();
	}
}
